import type { CandidateComp } from "./types";

/**
 * COMP SEARCH CRITERIA, CLIENT SIDE (fan-out 1G) — the wire shapes and the pure rules behind the
 * "Find comparable sales" panel and the map. The server half is `src/server/comp_search.py`; read
 * its docstring first. Nothing here decides what a comparable IS — it carries the agent's
 * question to the sources and reports honestly what came back.
 *
 *   * THE DEFAULTS ARE THE SERVER'S. `GET /api/next/comp-search/defaults` returns the default scope
 *     and the one widen (read server-side from the AMB comp-pull constants). This file spells no
 *     radius or window of its own; the panel does not render until it has them.
 *   * RE-RUN IS EXPLICIT. `criteriaToRequest` builds the body for one button press. Editing a field
 *     never fires a request, because every request can spend a provider allowance.
 *   * NOTHING THE AGENT HOLDS IS DELETED. A re-run replaces the provider's candidates, but a row the
 *     agent has ticked stays, and an uploaded row outside the criteria is flagged, not removed.
 */

export type CompStatus = "sold" | "active" | "pending";
/** `ingestion.home_type.HOME_TYPES` — what a record STATES, never a guess. */
export type HomeType = "single_family" | "townhome" | "condo";

export interface CompCriteria {
  radius_mi: number;
  months: number;
  /** ± percent of the subject's living area. Retired from the panel 2026-10-22 (square feet
   * replaced it); kept so a criteria set from an older tab still reads. null = no band. */
  gla_band_pct: number | null;
  /** ± square feet of the subject's living area, bounds inclusive; null = any size. */
  gla_band_sqft: number | null;
  /** Single-family / townhome / condo; empty = every type. */
  home_types: HomeType[];
  min_beds: number | null;
  min_baths: number | null;
  /** ± years around the subject's year built; null = no band. */
  year_band_years: number | null;
  same_subdivision: boolean;
  statuses: CompStatus[];
  /** ± percent of the subject's lot size, bounds inclusive; null = any lot. The automatic run starts
   * at 10 (R6, 2026-10-28). Optional on the wire so a criteria set from an older tab still reads. */
  lot_band_pct?: number | null;
  /** Hide comps that have no lot size on record. Off by default: they are kept and labeled. */
  hide_unknown_lot?: boolean;
  /** R7 acreage-estate mode. Absent on a standard search; when true the site-acres range replaces
   * the lot band. Sent to the server unchanged. */
  acreage_mode?: boolean;
  site_acres_min?: number | null;
  site_acres_max?: number | null;
}

export interface StatusAvailability {
  available: boolean;
  reason: string | null;
  locked_on?: boolean;
}

export interface CompSearchDefaults {
  defaults: CompCriteria;
  /** The living-area choices, in square feet (server: `comp_search.GLA_BAND_SQFT_OPTIONS`). */
  gla_band_sqft_options: number[];
  /** The lot-size choices, in percent (server: `comp_search.LOT_BAND_PCT_OPTIONS`). */
  lot_band_pct_options?: number[];
  home_types: { value: HomeType; label: string }[];
  widen: { radius_mi: number; months: number; when_fewer_than: number };
  ranges: Record<string, { min: number; max: number; step?: number }>;
  statuses: Record<CompStatus, StatusAvailability>;
}

export interface CompSourceCount {
  rung: string;
  label: string;
  status: string;
  found: number;
  matched: number;
  reason: string | null;
  /** `{field label: rows kept only because the record does not state the field}`. */
  unchecked: Record<string, number>;
  /** Rows kept in the "type not stated" group: counted in `found`, never in `matched`. */
  type_unstated?: number;
}

/** The subject's property type as the cascade read it, with its source, or why it is unknown. */
export interface SubjectHomeType {
  value: HomeType | null;
  label: string | null;
  source: string | null;
  reason: string | null;
}

/** R7: the `acreage` block of the comp-search summary. Present ONLY when acreage mode ran; an older
 * server or a standard brief has no such key and everything renders as it always has. */
export type AcreageTier = "same_area" | "adjacent" | "outer";
export interface AcreageBlock {
  acreage_mode: true;
  subject_acres: number | null;
  subject_acres_tag: "computed" | null;
  subject_acres_how: string | null;
  agent_picked: boolean;
  criteria: CompCriteria & { site_acres_min: number; site_acres_max: number };
  tiers: {
    counts: Record<AcreageTier, number>;
    labels: Record<AcreageTier, string>;
    unplaced: number;
    outer_needs_location_adjustment: boolean;
  };
  excluded_complex_lots: number;
  notes: string[];
}

/** The `comp_search` block of a cascade resolve. */
export interface CompSearchSummary {
  criteria: CompCriteria | null;
  applied: boolean;
  scope: { radius_mi: number; months: number };
  widened: boolean;
  widened_message: string | null;
  skipped: string[];
  sources: CompSourceCount[];
  /** True when `criteria` are the automatic search's (±200 sq ft, the subject's type). */
  auto?: boolean;
  /** Set when the living-area range left fewer sales than the floor; the range is never widened. */
  size_note?: string | null;
  /** Set when the lot-size range left fewer sales than the floor; the range is never widened. */
  lot_note?: string | null;
  /** The subject's lot in square feet as the automatic run used it, or null (filter not applied). */
  subject_lot_sf?: number | null;
  subject_home_type?: SubjectHomeType | null;
  /** R7: only when acreage mode ran. */
  acreage?: AcreageBlock | null;
}

export interface RowVerdict {
  matches: boolean;
  unchecked: string[];
}

/** The request body for `comp_criteria`: nulls are omitted, so "no band" is "not sent". */
export function criteriaToRequest(c: CompCriteria): Record<string, unknown> {
  const out: Record<string, unknown> = {
    radius_mi: c.radius_mi,
    months: c.months,
    statuses: c.statuses,
    same_subdivision: c.same_subdivision,
  };
  if (c.gla_band_sqft != null) out.gla_band_sqft = c.gla_band_sqft;
  else if (c.gla_band_pct != null) out.gla_band_pct = c.gla_band_pct;
  if (c.home_types?.length) out.home_types = c.home_types;
  if (c.lot_band_pct != null) out.lot_band_pct = c.lot_band_pct;
  if (c.hide_unknown_lot) out.hide_unknown_lot = true;
  // R7: passed through unchanged when present; absent keys are not invented.
  if (c.acreage_mode) out.acreage_mode = true;
  if (c.site_acres_min != null) out.site_acres_min = c.site_acres_min;
  if (c.site_acres_max != null) out.site_acres_max = c.site_acres_max;
  if (c.min_beds !== null) out.min_beds = c.min_beds;
  if (c.min_baths !== null) out.min_baths = c.min_baths;
  if (c.year_band_years !== null) out.year_band_years = c.year_band_years;
  return out;
}

export function sameCriteria(a: CompCriteria, b: CompCriteria): boolean {
  return JSON.stringify(criteriaToRequest(a)) === JSON.stringify(criteriaToRequest(b));
}

const miles = (n: number) => `${Number(n.toFixed(2))} mi`;

/** The words for each type, as the panel's labels use them. */
export const HOME_TYPE_WORDS: Record<HomeType, string> = {
  single_family: "single-family homes", townhome: "townhomes", condo: "condos",
};

/** A criteria set off the wire, with the 2026-10-22 keys present whatever sent it. */
export function normalizeCriteria(c: Partial<CompCriteria> & Pick<CompCriteria, "radius_mi" | "months">): CompCriteria {
  return {
    gla_band_pct: null, gla_band_sqft: null, home_types: [], min_beds: null, min_baths: null,
    year_band_years: null, same_subdivision: false, statuses: ["sold"],
    lot_band_pct: null, hide_unknown_lot: false,
    ...c,
  } as CompCriteria;
}

/** The criteria the panel starts from: the server's defaults, with the subject's own type
 * pre-selected when a record states it (`subject_home_type`), and none when it does not. */
export function startingCriteria(defaults: CompCriteria, subjectType: SubjectHomeType | null | undefined): CompCriteria {
  const base = normalizeCriteria(defaults);
  // R6: a condo's MLS lot is the whole complex, so the lot range starts OFF for a condo subject (the
  // panel says why: `LOT_OFF_CONDO_REASON`). The agent can turn it on.
  const lotOff = subjectType?.value === "condo";
  return { ...base, home_types: subjectType?.value ? [subjectType.value] : [], ...(lotOff ? { lot_band_pct: null } : {}) };
}

/** The lot default alone (no type pre-select): off for a condo subject, unchanged otherwise. */
export function startingLotOnly(c: CompCriteria, subjectType: SubjectHomeType | null | undefined): CompCriteria {
  return subjectType?.value === "condo" ? { ...c, lot_band_pct: null } : c;
}

/** Shown beside the lot-size control when the range starts off for a condo subject. */
export const LOT_OFF_CONDO_REASON =
  "Lot-size range is off for a condo: its MLS lot size is the whole complex, not this unit. Turn it on if you want it.";

/** "N sales don't state a property type." — the grouped rows, said in words, or null. */
export function typeUnstatedLine(s: CompSourceCount): string | null {
  const n = s.type_unstated ?? 0;
  if (!n) return null;
  return `${s.label}: ${n} ${n === 1 ? "sale doesn't" : "sales don't"} state a property type. ${n === 1 ? "It's" : "They're"} listed separately, not counted as a match.`;
}

/** The one-line summary of a criteria set, in the words the panel's labels use. */
export function describeCriteria(c: CompCriteria): string {
  const parts = [`${miles(c.radius_mi)}`, `sold in the last ${c.months} months`];
  if (c.gla_band_sqft != null) parts.push(`living area within ±${c.gla_band_sqft.toLocaleString("en-US")} sq ft`);
  else if (c.gla_band_pct != null) parts.push(`living area within ${c.gla_band_pct}%`);
  if (c.acreage_mode && c.site_acres_min != null && c.site_acres_max != null) {
    parts.push(`site ${c.site_acres_min}–${c.site_acres_max} acres`);
  }
  if (c.lot_band_pct != null) parts.push(`lot size within ±${c.lot_band_pct}%`);
  if (c.hide_unknown_lot) parts.push("lot size on record");
  if (c.home_types?.length) parts.push(c.home_types.map((t) => HOME_TYPE_WORDS[t]).join(" or "));
  if (c.min_beds !== null && c.min_beds > 0) parts.push(`${c.min_beds}+ beds`);
  if (c.min_baths !== null && c.min_baths > 0) parts.push(`${c.min_baths}+ baths`);
  if (c.year_band_years !== null) parts.push(`built within ${c.year_band_years} years`);
  if (c.same_subdivision) parts.push("same subdivision");
  const extra = c.statuses.filter((s) => s !== "sold");
  if (extra.length) parts.push(`plus ${extra.join(" and ")} listings`);
  return parts.join(" · ");
}

/** "MLS: 9 of 14 match" — found is what the source returned, matched is what is still in play. */
export function sourceCountLine(s: CompSourceCount): string {
  if (s.status === "unavailable") return `${s.label}: could not be reached`;
  if (s.status === "not_available") return `${s.label}: not available`;
  // The MLS search was never run because the home has no map location; the reason prints below.
  if (s.found === 0 && s.rung === "mls" && s.reason) return `${s.label}: not searched`;
  if (s.found === 0) return `${s.label}: none found`;
  if (s.found === s.matched) return `${s.label}: ${s.matched} found`;
  return `${s.label}: ${s.matched} of ${s.found} match`;
}

/** The "kept because the record does not say" footnote for one source, or null. */
export function uncheckedLine(s: CompSourceCount): string | null {
  const parts = Object.entries(s.unchecked ?? {})
    .filter(([field, n]) => n > 0 && field !== "property type")
    .map(([field, n]) => `${n} with no ${field} on record`);
  return parts.length ? `${s.label}: kept ${parts.join(", ")}.` : null;
}

/** A source worth listing: one that exists on this deployment. The county rung always answers
 * "not available" for sales (Texas does not publish prices), which is worth one honest line. */
export function visibleSources(summary: CompSearchSummary | null | undefined): CompSourceCount[] {
  return (summary?.sources ?? []).filter((s) => s.rung !== "agent");
}

// --------------------------------------------------------------------------------------------
// Re-run: what happens to the rows already on the screen
// --------------------------------------------------------------------------------------------

/** A row that came from a search (MLS or the public-record provider), as opposed to one the agent
 * brought. `mlsLabel` is the row tag the MLS lane writes. */
export function isSearchResult(
  c: Pick<CandidateComp, "_source">,
  rungLabels: Record<string, string> | null | undefined,
): boolean {
  const src = c._source;
  if (typeof src !== "string" || !src) return false;
  return src === rungLabels?.cotality || src === "NTREIS MLS";
}

/**
 * Replace the search's candidates with a fresh set, keeping everything the agent has a stake in.
 *
 *   kept    every row that is not a search result (uploaded or typed) — flagged by the caller, never
 *           removed here — and every search result the agent has ticked, even when it falls outside
 *           the new criteria (it is flagged `_outside_criteria` so the grid can say so).
 *   dropped the unticked search results from the last run.
 *   added   the new candidates, minus any already held (matched by the caller's address key).
 *
 * `keptOutside` is how many ticked rows survived although the new search would not have returned
 * them: the panel says so in words, so a ticked comp never vanishes silently.
 */
export function reconcileRerun(
  prev: CandidateComp[],
  incoming: CandidateComp[],
  rungLabels: Record<string, string> | null | undefined,
  key: (c: CandidateComp) => string,
): { comps: CandidateComp[]; keptOutside: number } {
  const fresh = new Map(incoming.map((c) => [key(c), c]));
  const kept: CandidateComp[] = [];
  let keptOutside = 0;
  for (const c of prev) {
    if (!isSearchResult(c, rungLabels)) {
      kept.push(c);
      continue;
    }
    if (!c._selected) continue; // an unticked candidate of the last search: replaced
    if (fresh.has(key(c))) {
      fresh.delete(key(c)); // still a result: keep the agent's row (their ticks and edits), drop the twin
      kept.push({ ...c, _outside_criteria: undefined });
    } else {
      keptOutside += 1;
      kept.push({ ...c, _outside_criteria: true });
    }
  }
  return { comps: [...kept, ...fresh.values()], keptOutside };
}

/** Flag (never remove) the rows the agent brought that fall outside the criteria. */
export function flagOutside(comps: CandidateComp[], isOwn: (c: CandidateComp) => boolean,
                            verdicts: RowVerdict[]): CandidateComp[] {
  let v = 0;
  return comps.map((c) => {
    if (!isOwn(c)) return c;
    const verdict = verdicts[v++];
    if (!verdict) return c;
    return verdict.matches ? { ...c, _outside_criteria: undefined } : { ...c, _outside_criteria: true };
  });
}

/** Per-source counts for the rows the agent brought, from the server's verdicts. */
export function ownSourceCount(label: string, verdicts: RowVerdict[]): CompSourceCount {
  const unchecked: Record<string, number> = {};
  for (const v of verdicts) for (const f of v.unchecked) unchecked[f] = (unchecked[f] ?? 0) + 1;
  return {
    rung: "files", label, status: verdicts.length ? "ok" : "not_available",
    found: verdicts.length, matched: verdicts.filter((v) => v.matches).length, reason: null, unchecked,
  };
}

/** A subject-grid value as a finite number, or null. Blank is null, never 0 (unknown is not zero). */
export function numFromGrid(v: unknown): number | null {
  if (v === null || v === undefined || typeof v === "boolean") return null;
  const t = String(v).replace(/,/g, "").trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

// --------------------------------------------------------------------------------------------
// R7 acreage-estate mode: words the screen prints. Nothing here prices anything.
// --------------------------------------------------------------------------------------------

/** The most the radius slider allows while acreage mode is on (server: `comp_acreage.RADIUS_MAX_MI`). */
export const ACREAGE_RADIUS_MAX_MI = 15;
/** What a figure with no supported rate shows. Never $0, never blank. */
export const NOT_PRICED = "not priced";
export const OUTER_RING_TEXT = "location adjustment needed";
export const CONTEXT_ONLY = "context only";

/** The values the automatic acreage run used, as a person reads them. */
export function acreageUsedLines(a: AcreageBlock): string[] {
  const c = a.criteria;
  const out = [
    `Radius ${Number(c.radius_mi.toFixed(2))} mi, ranked by distance`,
    `Sold in the last ${c.months} months`,
    `Site ${c.site_acres_min}–${c.site_acres_max} acres`,
  ];
  if (c.gla_band_pct != null) out.push(`Living area within ±${c.gla_band_pct}%`);
  else if (c.gla_band_sqft != null) out.push(`Living area within ±${c.gla_band_sqft.toLocaleString("en-US")} sq ft`);
  if (c.year_band_years != null) out.push(`Built within ±${c.year_band_years} years`);
  out.push(c.home_types?.length ? `Property type kept: ${c.home_types.map((t) => HOME_TYPE_WORDS[t]).join(" or ")}` : "Property type: any");
  return out;
}

/** "N condo or complex lots left out: their lot size is the whole complex.", or null for none. */
export function complexLotLine(n: number | null | undefined): string | null {
  if (!n || n < 1) return null;
  return `${n} condo or complex ${n === 1 ? "lot" : "lots"} left out: their lot size is the whole complex.`;
}

/** The ordered tier rows. A tier with no sales is still listed (with 0), so a gap is visible. */
export function acreageTierRows(a: AcreageBlock): { tier: AcreageTier; label: string; count: number; note: string | null }[] {
  return (["same_area", "adjacent", "outer"] as AcreageTier[]).map((tier) => ({
    tier, label: a.tiers.labels[tier], count: a.tiers.counts[tier] ?? 0,
    note: tier === "outer" ? OUTER_RING_TEXT : null,
  }));
}

/** A comp's combined acreage for a multi-parcel sale, from what the server computed. The client
 * renders the server's display string only; it never formats or adds a figure itself. A sale with
 * a parcel that has no size shows the server's sentence and no total. */
export function combinedSiteLine(c: Pick<CandidateComp, "_site_display" | "_site_tag" | "_site_how" | "_site_parcels" | "_site_unstated" | "_site_unstated_text">):
  { acres: string | null; tag: "computed" | null; how: string } | null {
  if (c._site_unstated) return { acres: null, tag: null, how: c._site_unstated_text ?? "Combined site not shown: a parcel has no size on record." };
  if (!c._site_display || c._site_tag !== "computed") return null;
  return { acres: c._site_display, tag: "computed", how: c._site_how ?? `${c._site_parcels ?? 2} parcels` };
}
